import { useRef, useEffect } from 'react';
import { Loader2, Check, X } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AgentIcon } from '@/components/common/AgentIcon';
import { ChatMessage } from './ChatMessage';
import { ChatMarkdown } from './chat-markdown';
import { cn } from '@/lib/utils';
import type {
  Message,
  ToolCall,
  FieldWidgetData,
  MemoryBankNoteWidgetData,
} from '@/context/agent-state.context';

/** Callbacks for widget actions */
interface WidgetActionCallbacks {
  onConfirmField?: (messageId: string) => void;
  onRejectField?: (messageId: string) => void;
  onConfirmNote?: (messageId: string) => void;
  onRejectNote?: (messageId: string) => void;
}

interface ChatMessageListProps {
  messages: Message[];
  streamingText?: string;
  isLoading: boolean;
  isLoadingHistory?: boolean;
  error?: string | null;
  activeToolCalls?: ToolCall[];
  welcomeContent?: string;
  emptyMessage?: string;
  className?: string;
  messageMaxWidth?: string;
  size?: 'sm' | 'default';
  /** Render prop for custom message actions (e.g., comparison button) */
  renderMessageActions?: (message: Message) => React.ReactNode;
  /** Voice transcription text to display */
  voiceTranscription?: string | null;
  /** Custom status message to show while loading (e.g., "Analyzing schema...") */
  loadingStatusMessage?: string;
  /** Callbacks for widget actions */
  widgetActions?: WidgetActionCallbacks;
}

export function ChatMessageList({
  messages,
  streamingText,
  isLoading,
  isLoadingHistory,
  error,
  activeToolCalls = [],
  welcomeContent,
  emptyMessage = 'Start a conversation with the AI assistant.',
  className,
  messageMaxWidth = 'max-w-[85%]',
  size = 'default',
  renderMessageActions,
  voiceTranscription,
  loadingStatusMessage,
  widgetActions,
}: ChatMessageListProps) {
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector(
        '[data-radix-scroll-area-viewport]',
      );
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages, streamingText, voiceTranscription]);

  const isWaitingForFirstToken =
    isLoading && !streamingText && messages.at(-1)?.role === 'user';

  const visibleMessages = messages.filter((m) => !m.hidden);
  const hasContent =
    visibleMessages.length > 0 ||
    welcomeContent ||
    isLoading ||
    streamingText ||
    voiceTranscription;

  const sizeStyles = {
    sm: {
      spacing: 'space-y-3 py-3',
      padding: 'px-3',
      iconSize: 20,
      toolCallText: 'text-xs',
      toolCallDot: 'w-1.5 h-1.5',
    },
    default: {
      spacing: 'space-y-4 py-4',
      padding: 'px-4',
      iconSize: 24,
      toolCallText: 'text-sm',
      toolCallDot: 'w-2 h-2',
    },
  };

  const styles = sizeStyles[size];

  // Helper to render widget UI for a message
  const renderWidget = (msg: Message) => {
    if (!msg.widget) return null;

    const { widget } = msg;
    const isPending = widget.status === 'pending';
    const isFieldWidget = widget.type === 'field';
    const isNoteWidget = widget.type === 'memoryBankNote';

    // Type guard helpers
    const fieldData = isFieldWidget ? (widget.data as FieldWidgetData) : null;
    const noteData = isNoteWidget
      ? (widget.data as MemoryBankNoteWidgetData)
      : null;

    // Resolved widgets (confirmed/rejected)
    if (!isPending) {
      const statusLabel = isFieldWidget ? 'Field' : 'Note';
      return (
        <div className="flex justify-start mt-2">
          <div
            className={cn(
              messageMaxWidth,
              'p-3 rounded-lg border',
              widget.status === 'confirmed'
                ? 'bg-green-50 border-green-200'
                : 'bg-red-50 border-red-200',
            )}
          >
            <div className="flex items-center gap-2 mb-2">
              {widget.status === 'confirmed' ? (
                <Check className="h-4 w-4 text-green-600" />
              ) : (
                <X className="h-4 w-4 text-red-600" />
              )}
              <p
                className={cn(
                  'text-sm font-medium',
                  widget.status === 'confirmed'
                    ? 'text-green-800'
                    : 'text-red-800',
                )}
              >
                {statusLabel} {widget.status}
              </p>
            </div>
            <div className="bg-white rounded border border-stone-200 p-2">
              {isFieldWidget && fieldData && (
                <>
                  <p className="font-medium text-sm text-stone-800 mb-1">
                    {fieldData.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {fieldData.description}
                  </p>
                </>
              )}
              {isNoteWidget && noteData && (
                <>
                  <p className="text-xs text-muted-foreground mb-1">
                    For: {noteData.entityLabel}
                  </p>
                  <p className="text-sm text-stone-800">{noteData.content}</p>
                </>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Pending widgets need action buttons
    if (isFieldWidget && fieldData) {
      const confirmHandler = widgetActions?.onConfirmField;
      const rejectHandler = widgetActions?.onRejectField;

      return (
        <div className="flex justify-start mt-2">
          <div
            className={cn(
              messageMaxWidth,
              'p-3 rounded-lg bg-stone-100 border border-stone-300',
            )}
          >
            <p className="text-sm font-medium text-stone-800 mb-2">
              Add new configured field?
            </p>
            <div className="bg-white rounded border border-stone-200 p-2 mb-3">
              <p className="font-medium text-sm text-stone-800 mb-1">
                {fieldData.name}
              </p>
              <p className="text-xs text-muted-foreground">
                {fieldData.description}
              </p>
            </div>
            <div className="flex gap-3">
              {confirmHandler && (
                <button
                  onClick={() => confirmHandler(msg.id)}
                  className="flex items-center gap-1 text-sm font-medium text-green-600 hover:text-green-700"
                >
                  <Check className="h-4 w-4" />
                  Confirm
                </button>
              )}
              {rejectHandler && (
                <button
                  onClick={() => rejectHandler(msg.id)}
                  className="text-sm font-medium text-red-600 hover:text-red-700"
                >
                  Reject
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (isNoteWidget && noteData) {
      const confirmHandler = widgetActions?.onConfirmNote;
      const rejectHandler = widgetActions?.onRejectNote;

      return (
        <div className="flex justify-start mt-2">
          <div
            className={cn(
              messageMaxWidth,
              'p-3 rounded-lg bg-stone-100 border border-stone-300',
            )}
          >
            <p className="text-sm font-medium text-stone-800 mb-2">
              Save this note?
            </p>
            <div className="bg-white rounded border border-stone-200 p-2 mb-3">
              <p className="text-xs text-muted-foreground mb-1">
                For: {noteData.entityLabel}
              </p>
              <p className="text-sm text-stone-800">{noteData.content}</p>
            </div>
            <div className="flex gap-3">
              {confirmHandler && (
                <button
                  onClick={() => confirmHandler(msg.id)}
                  className="flex items-center gap-1 text-sm font-medium text-green-600 hover:text-green-700"
                >
                  <Check className="h-4 w-4" />
                  Confirm
                </button>
              )}
              {rejectHandler && (
                <button
                  onClick={() => rejectHandler(msg.id)}
                  className="text-sm font-medium text-red-600 hover:text-red-700"
                >
                  Reject
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  if (isLoadingHistory) {
    return (
      <ScrollArea
        ref={scrollAreaRef}
        className={cn('flex-1 min-h-0', styles.padding, className)}
      >
        <div className="flex items-center justify-center h-full py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </ScrollArea>
    );
  }

  if (!hasContent) {
    return (
      <ScrollArea
        ref={scrollAreaRef}
        className={cn('flex-1 min-h-0', styles.padding, className)}
      >
        <div className="flex items-center justify-center h-full py-8">
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        </div>
      </ScrollArea>
    );
  }

  return (
    <ScrollArea
      ref={scrollAreaRef}
      className={cn('flex-1 min-h-0', styles.padding, className)}
    >
      <div className={styles.spacing}>
        {welcomeContent && (
          <div className="flex justify-start">
            <div
              className={cn(
                messageMaxWidth,
                'p-2.5 rounded-lg bg-stone-100 text-stone-800 border border-stone-300',
              )}
            >
              <ChatMarkdown>{welcomeContent}</ChatMarkdown>
            </div>
          </div>
        )}

        {visibleMessages.map((msg) => (
          <div key={msg.id}>
            <ChatMessage
              role={msg.role}
              content={msg.content}
              maxWidth={messageMaxWidth}
            />
            {renderMessageActions?.(msg)}
            {renderWidget(msg)}
          </div>
        ))}

        {voiceTranscription != null && (
          <div className="flex justify-end">
            <div
              className={cn(
                messageMaxWidth,
                'p-2.5 rounded-lg bg-stone-700 text-stone-50 opacity-80',
              )}
            >
              <p className="text-sm whitespace-pre-wrap break-words">
                {voiceTranscription || (
                  <span className="text-stone-400 italic">Listening...</span>
                )}
              </p>
            </div>
          </div>
        )}

        {isWaitingForFirstToken && (
          <div className="flex justify-start">
            <div className="flex items-center gap-2 p-2.5">
              <AgentIcon state="loading" size={styles.iconSize} />
              {loadingStatusMessage && (
                <span className="text-sm text-stone-500 animate-pulse">
                  {loadingStatusMessage}
                </span>
              )}
            </div>
          </div>
        )}

        {isLoading && streamingText && (
          <div className="flex justify-start">
            <div
              className={cn(
                messageMaxWidth,
                'p-2.5 rounded-lg bg-stone-100 text-stone-800 border border-stone-300 opacity-80',
              )}
            >
              <ChatMarkdown>{streamingText}</ChatMarkdown>
            </div>
          </div>
        )}

        {error && (
          <div className="flex justify-start">
            <div
              className={cn(
                messageMaxWidth,
                'p-2.5 rounded-lg bg-red-100 text-red-700 border border-red-300',
              )}
            >
              <p className="text-sm font-medium">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {activeToolCalls.length > 0 && (
          <div className="space-y-1">
            {activeToolCalls
              .filter((tc) => tc.status === 'pending')
              .map((tc) => (
                <div
                  key={tc.toolCallId}
                  className={cn(
                    'flex items-center gap-2 text-stone-500 animate-pulse',
                    styles.toolCallText,
                  )}
                >
                  <span
                    className={cn(
                      'inline-block bg-stone-500 rounded-full',
                      styles.toolCallDot,
                    )}
                  />
                  Working on {tc.toolName}...
                </div>
              ))}
          </div>
        )}
      </div>
    </ScrollArea>
  );
}
